/*
 * HomeCrest Layer 3 - Configuration + application
 * ===========================================================================
 * Turns validated intent into the configuration object that is the source of
 * truth for everything downstream: preview, submission, manufacture.
 *
 *   validated intent
 *        |
 *        +-- variant_selections --> applied through Horizon's variant picker
 *        |                          (price, SKU, gallery, add-to-cart target)
 *        |
 *        +-- modifications ------> configuration ONLY. Never touches Shopify.
 *
 * OPEN-ENDED BY DESIGN (schema 2)
 *
 * There is no list of permitted modifications. A customer may ask for anything;
 * an unrecognised request becomes a `structural` modification carrying the
 * normalised instruction rather than being refused. What remains closed is the
 * PRESERVATION contract in Layer 4 - what the image model may alter - not what
 * the customer may say.
 *
 * `modifications` is an ORDERED stack. "Make it U-shaped" then "remove the
 * middle section" is a different design from the reverse, so order is part of
 * the identity of the design and part of the hash.
 *
 * TWO RULES STILL DO THE HEAVY LIFTING
 *
 * 1. There is no second variant engine. Selections are applied by clicking the
 *    input Horizon already renders. This module never computes a variant id.
 *
 * 2. `fulfilment` and `purchasable` are derived here and deeply frozen. The
 *    frontend cannot author them and cannot mutate a configuration after it has
 *    been hashed. Any modification at all means not purchasable.
 *
 * Nothing here renders. No DOM belonging to the PDP is created or restyled.
 */

const CONFIG_SCHEMA = 'homecrest.configuration/2';

/* ==========================================================================
   Canonicalisation + hash
   --------------------------------------------------------------------------
   The hash identifies a design so a preview or submission can be tied to
   exactly the thing the customer approved. Same design, same hash, always.

   `variant_selections` are SORTED - choosing finish before size is the same
   product. `modifications` are NOT sorted - their order is the design.

   `id` and `source_text` are excluded: they describe how the design was
   arrived at, not what it is, so they must not change the hash.
   ========================================================================== */

function canonicalise({ baseVariantId, variantSelections, modifications }) {
  const byAxis = (a, b) => String(a.axis).localeCompare(String(b.axis));

  return JSON.stringify({
    base_variant_id: baseVariantId == null ? null : String(baseVariantId),
    variant_selections: [...(variantSelections ?? [])]
      .map((s) => ({ axis: s.axis, value: s.value }))
      .sort(byAxis),
    modifications: [...(modifications ?? [])].map((m) => ({
      type: m.type,
      families: [...(m.families ?? [])].sort(),
      instruction: m.instruction ?? '',
      axis: m.axis ?? null,
      value: m.value ?? null,
      unit: m.unit ?? null,
    })),
  });
}

async function hash(canonicalString) {
  const bytes = new TextEncoder().encode(canonicalString);
  const digest = await crypto.subtle.digest('SHA-256', bytes);
  const hex = [...new Uint8Array(digest)].map((b) => b.toString(16).padStart(2, '0')).join('');
  return `sha256:${hex}`;
}

/** Short, human-quotable identity for a design. Derived, never stored. */
function designId(configurationHash) {
  return `HC-${String(configurationHash).replace('sha256:', '').slice(0, 8).toUpperCase()}`;
}

/* ==========================================================================
   Build
   ========================================================================== */

function deepFreeze(value) {
  if (value && typeof value === 'object') {
    Object.getOwnPropertyNames(value).forEach((k) => deepFreeze(value[k]));
    Object.freeze(value);
  }
  return value;
}

let modSeq = 0;

/** Normalises one modification and gives it a stable id within the session. */
function makeModification(raw) {
  modSeq += 1;
  return {
    id: `m${modSeq}`,
    seq: modSeq,
    type: raw.type ?? 'structural',
    instruction: String(raw.instruction ?? '').trim(),
    axis: raw.axis ?? null,
    value: raw.value ?? null,
    unit: raw.unit ?? null,
    recognised: raw.recognised === true,
    families: Array.isArray(raw.families) ? [...raw.families] : [],
    source_text: String(raw.source_text ?? '').trim(),
  };
}

/**
 * Builds the configuration. Pure apart from the digest, so it can be unit
 * tested against a fixture capability with no page present.
 *
 * @param {object}   input
 * @param {object[]} input.selections     Validated variant selections.
 * @param {object[]} input.modifications  The ORDERED design stack.
 * @param {object}   input.capability     The Layer 0 capability object.
 * @param {string|number|null} input.baseVariantId Resolved by Horizon.
 */
async function build({ selections = [], modifications = [], capability, baseVariantId = null, confidence = 'medium' }) {
  const variantSelections = selections.map((s) => ({ ...s }));
  const mods = modifications.map((m) => ({ ...m }));

  // DERIVED. Never read from input - a caller cannot assert purchasability.
  const isCustomised = mods.length > 0;
  const fulfilment = isCustomised ? 'customised' : 'standard';
  const purchasable = !isCustomised;

  const canonical = canonicalise({ baseVariantId, variantSelections, modifications: mods });
  const configuration_hash = await hash(canonical);

  return deepFreeze({
    schema: CONFIG_SCHEMA,
    product_id: capability?.product?.id ?? null,
    product_handle: capability?.product?.handle ?? null,
    product_title: capability?.product?.title ?? null,
    base_variant_id: baseVariantId == null ? null : String(baseVariantId),

    design_id: designId(configuration_hash),
    variant_selections: variantSelections,
    modifications: mods,

    fulfilment,
    purchasable,
    configuration_hash,

    // Contract only. No image is generated and none is rendered here.
    visual_preview: {
      status: 'pending',
      generated_from: configuration_hash,
      image_url: null,
      is_representation: true,
    },

    validation: {
      validated_by: 'homecrest.validator/2',
      capability_schema: capability?.schema ?? null,
      confidence,
      at: new Date().toISOString(),
    },
  });
}

/* ==========================================================================
   The design stack
   --------------------------------------------------------------------------
   Append-only, with explicit removal. The shipped resolver cannot tell
   "actually, make it L-shaped instead" (a correction) from "and also remove the
   middle section" (an addition) without a language model, and guessing wrong
   would silently discard part of the customer's design. So every request
   appends, and the customer is given a visible list they can remove from.
   ========================================================================== */

function appendModifications(stack, incoming) {
  return [...stack, ...incoming.map(makeModification)];
}

function removeModification(stack, id) {
  return stack.filter((m) => m.id !== id);
}

function undoLast(stack) {
  return stack.slice(0, -1);
}

/* ==========================================================================
   Application - real variant options only
   ========================================================================== */

function pickerIn(root) {
  return (root ?? document).querySelector('variant-picker');
}

function variantIdIn(root) {
  const scope = pickerIn(root)?.closest('.shopify-section') ?? root ?? document;
  return scope.querySelector('input[name="id"]')?.value ?? null;
}

/**
 * Applies variant selections by clicking the inputs Horizon already renders.
 *
 * Modifications are never passed here. A bespoke change is not a variant and
 * must not be pushed at a picker that has no concept of it.
 */
async function applyVariantSelections({ selections, capability, root = document, timeout = 6000 }) {
  const picker = pickerIn(root);
  const applied = [];
  const skipped = [];

  if (!picker || !selections?.length) {
    return { applied, skipped, baseVariantId: variantIdIn(root) };
  }

  const sourceByAxis = new Map((capability?.selectable ?? []).map((a) => [a.axis, a.sourceOption]));

  for (const selection of selections) {
    const sourceOption = sourceByAxis.get(selection.axis);
    if (!sourceOption) {
      skipped.push({ ...selection, reason: 'no source option' });
      continue;
    }

    const input = [...picker.querySelectorAll('.variant-option input')].find(
      (i) => i.dataset.optionName === sourceOption && i.value === selection.value
    );

    if (!input) {
      skipped.push({ ...selection, reason: 'input not found' });
      continue;
    }
    if (input.checked) {
      applied.push({ ...selection, changed: false });
      continue;
    }

    const before = variantIdIn(root);
    input.click();

    // Wait for Horizon to finish its own update rather than racing it.
    const started = Date.now();
    while (Date.now() - started < timeout) {
      await new Promise((r) => setTimeout(r, 120));
      if (variantIdIn(root) !== before) break;
    }
    applied.push({ ...selection, changed: true });
  }

  return { applied, skipped, baseVariantId: variantIdIn(root) };
}

/* ==========================================================================
   End to end
   ========================================================================== */

/**
 * request -> Layer 2 resolve -> apply selections -> configuration.
 *
 * `stack` is the design so far. New modifications are appended to it, so a
 * refinement builds on the previous design instead of replacing it.
 */
async function create({ request, capability, root = document, apply = true, stack = [] }) {
  const Intent = window.HomeCrestIntent;
  if (!Intent) {
    return { ok: false, errors: [{ code: 'no_intent_layer', message: 'Layer 2 not loaded.' }], configuration: null };
  }

  const cap = capability ?? Intent.readCapability(root);
  if (!cap) {
    return { ok: false, errors: [{ code: 'no_capability', message: 'No capability object.' }], configuration: null };
  }

  const result = await Intent.resolve({ request, capability: cap });
  if (!result.ok) return { ok: false, errors: result.errors, configuration: null, validated: null };

  let baseVariantId = variantIdIn(root);
  let application = { applied: [], skipped: [], baseVariantId };

  if (apply) {
    application = await applyVariantSelections({
      selections: result.validated.selections,
      capability: cap,
      root,
    });
    baseVariantId = application.baseVariantId;
  }

  const modifications = appendModifications(stack, result.validated.modifications ?? []);

  const configuration = await build({
    selections: result.validated.selections,
    modifications,
    capability: cap,
    baseVariantId,
    confidence: result.validated.confidence,
  });

  return { ok: true, errors: [], configuration, validated: result.validated, application, modifications };
}

/** Rebuilds a configuration from an edited stack, without re-interpreting text. */
async function rebuild({ capability, root = document, stack = [], selections = [] }) {
  const Intent = window.HomeCrestIntent;
  const cap = capability ?? Intent?.readCapability(root);
  return build({
    selections,
    modifications: stack,
    capability: cap,
    baseVariantId: variantIdIn(root),
  });
}

window.HomeCrestConfiguration = {
  SCHEMA: CONFIG_SCHEMA,
  canonicalise,
  hash,
  designId,
  build,
  rebuild,
  makeModification,
  appendModifications,
  removeModification,
  undoLast,
  applyVariantSelections,
  create,
};
